import { gpCommon } from '../../app';
import dictationButtonTemplate from './dictation-button.html';
import { DictationService } from '../../services/dictation-service';
import {
    DictationError,
    DictationSession,
    DictationState,
    isDictationSupported,
} from '../../dictation/dictation-session';
import { TranscriptBuffer } from '../../dictation/transcript-buffer';
import { isAiDictationEnabled } from '../../util/feature-flags';

/**
 * Speech-silence ceiling before a live mic finishes itself: long enough
 * that a thinking pause mid-dictation survives, short enough that a
 * walked-away mic doesn't keep billing (dictation is per-minute) or
 * listening. Measured on speech events, not audio level.
 */
const SILENCE_AUTO_STOP_MS = 60_000;

/**
 * Microphone button for any bound text field (issue #403).
 *
 *     <gp-dictation-button text="$ctrl.composerText" disabled="$ctrl.isSending">
 *
 * Deliberately does NOT own or wrap the field. The consumer keeps its own
 * textarea with its own classes, keydown handlers and validation; this
 * component only reads and writes the bound string. That is what lets the same
 * control sit in the chat composer's action row and inside the get-started
 * prompt box without either layout bending to it.
 *
 * Because it has no handle on the textarea it cannot listen for typing, so
 * reconciliation happens through TranscriptBuffer's abandonment rule — see
 * that file for why.
 */
export class DictationButtonComponent {
    public static readonly $inject = ['$scope', '$element', DictationService.serviceName];

    /** Two-way bound target field. */
    public text: string | null = null;
    /** Consumer-driven disable (e.g. while a message is sending). */
    public disabled = false;
    public ariaLabel: string | null = null;
    /** Attributed onto the usage row when dictating inside a plan. */
    public planId: string | null = null;
    /**
     * Optional: called once a recording finishes — by Enter or by clicking the
     * live mic — with the transcript settled in the bound field. Exists so a
     * consumer can take focus back and let the NEXT Enter send through the
     * field's own handler: Enter to finish and review, Enter again to send.
     * Without it the recording still finishes; the consumer just doesn't get
     * the focus handoff.
     */
    public onAccept?: () => void;
    /** Optional: lets a consumer reflect listening state in its own UI (e.g. a
     *  hint that switches to "Enter to send · Esc to stop"). */
    public onListeningChange?: (locals: { listening: boolean }) => void;

    /** False hides the control entirely — an unusable mic should be absent, not disabled. */
    public available = false;
    public state: DictationState = 'idle';
    public errorMessage: string | null = null;

    private session: DictationSession | null = null;
    private buffer = new TranscriptBuffer();
    private pageHideHandler: (() => void) | null = null;
    private keydownHandler: ((ev: KeyboardEvent) => void) | null = null;

    // --- Listening clock + silence guard ----------------------------------
    // The clock reassures the user the mic is genuinely live (and hints at
    // cost: dictation bills by the minute). The silence guard finishes a
    // forgotten live mic. It finalizes rather than stops, so everything said
    // before the silence is kept — and it deliberately does NOT run the
    // accept flow: stealing focus minutes after the user walked away would
    // misdirect their typing.
    private listeningTicker: number | null = null;
    private listeningStartedAt = 0;
    private lastSpeechAt = 0;
    public elapsedLabel = '';

    constructor(
        private $scope: ng.IScope,
        private $element: ng.IAugmentedJQuery,
        private gpDictation: DictationService,
    ) {}

    public $onInit(): void {
        this.available = isAiDictationEnabled() && isDictationSupported();
        if (!this.available) return;

        // The user taking the field back ends the session — typing over it, or
        // hitting send and having the consumer clear it. Watched rather than
        // only checked when a transcript lands, so typing into a silent mic
        // stops it immediately instead of whenever the next word is spoken.
        this.$scope.$watch(
            () => this.text,
            () => this.abandonIfUserIntervened(),
        );

        this.session = this.gpDictation.create(this, {
            onState: (state) => this.onState(state),
            onInterim: (text) => this.onInterim(text),
            onSegment: (id, text) => this.onSegment(id, text),
            onSegmentRevised: (id, text) => this.onSegmentRevised(id, text),
            onError: (error) => this.onError(error),
        });

        // A closing tab must not lose the session's usage, and a beacon flush
        // (the `true`) is the only kind that reliably survives pagehide.
        this.pageHideHandler = () => {
            if (this.session && this.state !== 'idle') this.session.flushUsage(true);
        };
        window.addEventListener('pagehide', this.pageHideHandler);
    }

    /**
     * A consumer disabling the control ends the session, it does not merely
     * grey the button out. Consumers disable while acting on the text the user
     * just committed, so by then the user has moved on and a still-live
     * microphone is one they believe is off.
     *
     * Handled here rather than at each consumer so any future surface gets it
     * by binding `disabled`, which it would do anyway.
     */
    public $onChanges(changes: ng.IOnChangesObject): void {
        const disabled = changes['disabled'];
        if (!disabled || disabled.isFirstChange()) return;
        if (!disabled.currentValue) return;
        if (this.session && this.state !== 'idle') {
            void this.session.stop();
        }
    }

    private startListeningClock(): void {
        if (this.listeningTicker !== null) return;
        this.listeningStartedAt = Date.now();
        this.lastSpeechAt = this.listeningStartedAt;
        this.updateElapsed();
        this.listeningTicker = window.setInterval(() => {
            this.updateElapsed();
            if (Date.now() - this.lastSpeechAt >= SILENCE_AUTO_STOP_MS && this.state === 'listening') {
                // Finalize, not stop, and no accept flow — see the field
                // comment above. The conditional blur defuses the
                // next-Enter-restarts trap without ever stealing focus from
                // wherever the user went.
                void this.gpDictation.finalizeActive();
                this.blurOwnButton();
            }
            this.$scope.$applyAsync();
        }, 1000);
    }

    private stopListeningClock(): void {
        if (this.listeningTicker !== null) {
            window.clearInterval(this.listeningTicker);
            this.listeningTicker = null;
        }
        this.elapsedLabel = '';
    }

    private updateElapsed(): void {
        const seconds = Math.max(0, Math.floor((Date.now() - this.listeningStartedAt) / 1000));
        this.elapsedLabel = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
    }

    public $onDestroy(): void {
        this.detachKeys();
        this.stopListeningClock();
        if (this.pageHideHandler) {
            window.removeEventListener('pagehide', this.pageHideHandler);
            this.pageHideHandler = null;
        }
        if (this.session && this.state !== 'idle') {
            void this.session.stop();
        }
    }

    // --- Keyboard control while listening ---------------------------------
    //
    // Recording is a quasi-modal state: after clicking the mic, focus sits on
    // the button (the component deliberately has no handle on the textarea),
    // so field-level keydown handlers never see the keys the user reaches for
    // — hence a document-level CAPTURE listener for the duration.
    //
    // Two-step shape as Crane asked for (2026-08-06): Enter finishes the
    // recording for review and hands focus back, so on Enter-means-send
    // surfaces the next Enter submits. Esc is layered dismiss — the first Esc
    // kills the mic and must NOT also dismiss a surrounding dialog.

    private attachKeys(): void {
        if (this.keydownHandler) return;
        this.keydownHandler = (ev) => this.onGlobalKeydown(ev);
        document.addEventListener('keydown', this.keydownHandler, true);
    }

    private detachKeys(): void {
        if (!this.keydownHandler) return;
        document.removeEventListener('keydown', this.keydownHandler, true);
        this.keydownHandler = null;
    }

    private onGlobalKeydown(ev: KeyboardEvent): void {
        if (this.state !== 'listening') return;

        if (ev.key === 'Escape') {
            ev.preventDefault();
            // stopImmediatePropagation, not stopPropagation: rival Esc
            // handlers are document-level CAPTURE listeners on this same node,
            // which stopPropagation cannot suppress. This only wins when our
            // listener registered first, which holds when the recording
            // predates the other surface.
            ev.stopImmediatePropagation();
            this.$scope.$applyAsync(() => {
                if (this.session && this.state === 'listening') void this.session.stop();
                // Same trap as the Enter path: a still-focused mic button
                // turns the user's next Enter into a click that starts a
                // NEW recording.
                this.blurOwnButton();
            });
            return;
        }

        if (ev.key !== 'Enter' || ev.shiftKey || ev.metaKey || ev.ctrlKey || ev.altKey || ev.isComposing) return;
        if (isTextEntry(ev.target)) return;
        ev.preventDefault();
        ev.stopImmediatePropagation();
        // acceptRecording's blur is what defuses the next-Enter-restarts trap
        // described above.
        this.acceptRecording();
    }

    private blurOwnButton(): void {
        const btn = this.$element[0]?.querySelector('button.gp-dictation-btn');
        if (btn instanceof HTMLElement && document.activeElement === btn) btn.blur();
    }

    public get isListening(): boolean {
        return this.state === 'listening';
    }

    public get isBusy(): boolean {
        return this.state === 'requesting-permission' || this.state === 'connecting' || this.state === 'stopping';
    }

    public get iconClass(): string {
        if (this.isBusy) return 'fa fa-spinner fa-spin';
        if (this.state === 'error') return 'fa fa-microphone-slash';
        return 'fa fa-microphone';
    }

    public get title(): string {
        if (this.errorMessage) return this.errorMessage;
        if (this.isListening) return 'Listening. Enter or Esc stops dictating.';
        return 'Dictate — click and speak';
    }

    public get label(): string {
        return this.ariaLabel || (this.isListening ? 'Stop dictating' : 'Dictate');
    }

    public toggle(): void {
        if (!this.session || this.disabled || this.isBusy) return;

        if (this.state === 'listening') {
            // Same path as Enter, so "Enter to send" is immediately true after
            // a click-stop too (previously focus stayed on the button and
            // Enter restarted the mic).
            this.acceptRecording();
            return;
        }

        this.errorMessage = null;
        this.buffer.start(this.text || '');
        void this.session.start({ planId: this.planId });
    }

    /** Lossless finalize — the tail is kept — then focus back through
     *  on-accept so the next Enter sends on surfaces that wire it. */
    private acceptRecording(): void {
        void this.gpDictation.finalizeActive().then(() => {
            this.$scope.$applyAsync(() => {
                this.blurOwnButton();
                if (this.onAccept) this.onAccept();
            });
        });
    }

    private onState(state: DictationState): void {
        const wasListening = this.state === 'listening';
        this.state = state;
        const listening = state === 'listening';
        if (listening) { this.attachKeys(); this.startListeningClock(); }
        else { this.detachKeys(); this.stopListeningClock(); }
        if (listening !== wasListening && this.onListeningChange) {
            this.onListeningChange({ listening });
        }
        if (state === 'idle' || state === 'error') {
            // Anything half-spoken when the session ended never settled into a
            // transcript, so it must not be left sitting in the field.
            this.buffer.discardInterim();
            this.write();
        }
    }

    private onInterim(text: string): void {
        this.lastSpeechAt = Date.now();
        if (this.abandonIfUserIntervened()) return;
        this.buffer.setInterim(text);
        this.write();
    }

    private onSegment(id: number, text: string): void {
        this.lastSpeechAt = Date.now();
        if (this.abandonIfUserIntervened()) return;
        this.buffer.addSegment(id, text);
        this.write();
    }

    /**
     * The cleanup returned for a chunk. Dropped entirely if the user has since
     * taken the field back — a late cleanup writing itself in is what used to
     * resurrect deleted speech.
     */
    private onSegmentRevised(id: number, text: string): void {
        if (this.abandonIfUserIntervened()) return;
        if (this.buffer.reviseSegment(id, text)) this.write();
    }

    private onError(error: DictationError): void {
        this.errorMessage = describeError(error);
    }

    /**
     * Hand the field back to the user the moment they touch it, and report
     * whether that has happened so callers drop whatever they were writing.
     *
     * STICKY, AND NOT CONDITIONED ON SESSION STATE. The previous version only
     * looked for intervention while listening or connecting, so a cleanup
     * response that arrived after the session had stopped read as "no
     * intervention" and wrote its text into a field the user had just cleared —
     * speech they deleted, reappearing seconds later. Once abandoned the buffer
     * refuses every subsequent write, whenever it arrives.
     *
     * Deliberately covers the consumer clearing the field after a send as well
     * as literal typing: pressing send IS the user intervening, and leaving the
     * microphone live afterwards would be a live mic the user believes is off.
     */
    private abandonIfUserIntervened(): boolean {
        if (this.buffer.isAbandoned) return true;

        const change = this.buffer.classifyChange(this.text || '');
        if (change === 'none') return false;
        if (change === 'benign') {
            // Punctuating as you think is not taking the field back. Absorb it
            // and keep listening — stopping here made the mic hair-trigger.
            this.buffer.absorbBenign(this.text || '');
            return false;
        }

        this.buffer.abandon();
        if (this.session && this.state !== 'idle') void this.session.stop();
        return true;
    }

    private write(): void {
        this.buffer.writeTo((value) => {
            this.text = value;
        });
    }
}

/**
 * Error copy. The session module reports codes, not messages, so wording lives
 * here — one place, and the module stays testable without asserting on prose.
 */
function describeError(error: DictationError): string {
    switch (error.code) {
        case 'permission-denied':
            return 'Microphone blocked. Allow microphone access in your browser, then try again.';
        case 'no-microphone':
            return 'No microphone found. Connect one and try again.';
        case 'microphone-busy':
            return 'Your microphone is in use by another app.';
        case 'forbidden':
            return 'Dictation is not enabled for your account.';
        case 'rate-limited':
            return 'Too many dictation sessions just now. Try again in a minute.';
        case 'connect-failed':
            return 'Could not connect for dictation. Your network may block it.';
        case 'network':
            return 'Network problem starting dictation. Check your connection.';
        default:
            return 'Dictation is unavailable right now.';
    }
}

/** Enter routing: inside a field the field's own handler owns the key. */
function isTextEntry(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT' || target.isContentEditable;
}

gpCommon.component('gpDictationButton', {
    template: dictationButtonTemplate,
    controller: DictationButtonComponent,
    bindings: {
        text: '=',
        disabled: '<?',
        ariaLabel: '@?',
        planId: '<?',
        onAccept: '&?',
        onListeningChange: '&?',
    },
});
